import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import Sidebar from '../components/Sidebar';
import { 
  ArrowLeft, MapPin, CheckCircle, Globe, Phone, Clock, 
  Calendar, Users, Instagram, Facebook, Twitter, Linkedin,
  Building2, Share2, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

const CATEGORY_CONFIG = {
  dining: { icon: '🍽️', name: 'Dining' },
  fitness: { icon: '💪', name: 'Fitness / Sports' },
  music: { icon: '🎵', name: 'Music / Entertainment' },
  wellness: { icon: '🧘', name: 'Wellness' },
  gaming: { icon: '🎮', name: 'Gaming' },
  arts: { icon: '🎨', name: 'Arts / Workshops' },
  local_services: { icon: '🏪', name: 'Local Services' },
  outdoor: { icon: '🏕️', name: 'Outdoor / Adventure' },
  social: { icon: '🤝', name: 'Social / Networking' },
};

const SOCIAL_ICONS = {
  instagram: Instagram,
  facebook: Facebook,
  twitter: Twitter,
  linkedin: Linkedin,
};

export default function BusinessProfile() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState(null);
  const [badges, setBadges] = useState([]);
  const [hostedEvents, setHostedEvents] = useState([]);

  useEffect(() => {
    const loadBusinessProfile = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/api/business/${businessId}`);
        setBusiness(res.data.business);
        setBadges(res.data.badges || []);
        setHostedEvents(res.data.hosted_events || []);
      } catch (error) {
        if (error.response?.status === 404) {
          toast.error('Business not found');
          navigate('/directory');
        } else {
          toast.error('Failed to load business profile');
        }
      } finally {
        setLoading(false);
      }
    };
    loadBusinessProfile();
  }, [businessId, navigate]);

  const fetchBusinessProfile = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/business/${businessId}`);
      setBusiness(res.data.business);
      setBadges(res.data.badges || []);
      setHostedEvents(res.data.hosted_events || []);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Business not found');
        navigate('/directory');
      } else {
        toast.error('Failed to load business profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: business?.business_name,
        text: business?.business_description,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const getCategoryInfo = (categoryId) => {
    return CATEGORY_CONFIG[categoryId] || { icon: '🏢', name: categoryId };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-bold mb-2">Business not found</h2>
          <Button onClick={() => navigate('/directory')}>Back to Directory</Button>
        </div>
      </div>
    );
  }

  const category = getCategoryInfo(business.business_category);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b-2 border-black sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="hover:bg-slate-100 p-2 rounded">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <span className="font-medium">Business Profile</span>
          </div>
          <Button variant="outline" size="sm" onClick={handleShare} className="border-2 border-black">
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
        </div>
      </div>

      {/* Banner */}
      <div className="h-48 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 relative">
        <div className="max-w-4xl mx-auto px-4 h-full flex items-end pb-4">
          {business.profile_picture ? (
            <img 
              src={business.profile_picture} 
              alt={business.business_name}
              className="w-32 h-32 rounded-2xl border-4 border-white object-cover shadow-lg"
            />
          ) : (
            <div className="w-32 h-32 rounded-2xl border-4 border-white bg-white flex items-center justify-center text-5xl shadow-lg">
              {category.icon}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Main Info */}
        <div className="bg-white border-2 border-black rounded-xl p-6 shadow-brutal mb-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-2xl font-bold">{business.business_name}</h1>
                {(business.verification_status === 'verified' || business.business_verified) && (
                  <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1" data-testid="business-verified-badge">
                    <CheckCircle className="w-3 h-3" /> Verified
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-500">
                <span className="bg-slate-100 px-3 py-1 rounded-full">
                  {category.icon} {category.name}
                </span>
                {business.business_visibility_tier === 'market_sponsor' && (
                  <span className="bg-yellow-100 text-yellow-700 px-3 py-1 rounded-full">
                    ⭐ Market Sponsor
                  </span>
                )}
                {business.business_visibility_tier === 'local_featured' && (
                  <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full">
                    📍 Local Featured
                  </span>
                )}
              </div>
            </div>
          </div>

          {business.business_description && (
            <p className="text-slate-600 mb-6">{business.business_description}</p>
          )}

          {/* Contact & Location */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Location */}
            {(business.business_city || business.business_state) && (
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <div className="font-medium">Location</div>
                  <div className="text-sm text-slate-600">
                    {business.business_location_public && business.business_address_line1 && (
                      <div>{business.business_address_line1}</div>
                    )}
                    {business.business_location_public && business.business_address_line2 && (
                      <div>{business.business_address_line2}</div>
                    )}
                    <div>
                      {business.business_city}{business.business_city && business.business_state && ', '}{business.business_state}
                      {business.business_location_public && business.business_postal_code && ` ${business.business_postal_code}`}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Phone */}
            {business.business_phone_public && (
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <Phone className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <div className="font-medium">Phone</div>
                  <a href={`tel:${business.business_phone_public}`} className="text-sm text-indigo-600 hover:underline">
                    {business.business_phone_public}
                  </a>
                </div>
              </div>
            )}

            {/* Website */}
            {business.business_website && (
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <Globe className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <div className="font-medium">Website</div>
                  <a 
                    href={business.business_website} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-indigo-600 hover:underline flex items-center gap-1"
                  >
                    {business.business_website.replace(/^https?:\/\//, '')}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
            )}

            {/* Hours */}
            {business.business_hours && Object.keys(business.business_hours).length > 0 && (
              <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg">
                <Clock className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <div className="font-medium">Hours</div>
                  <div className="text-sm text-slate-600">
                    {Object.entries(business.business_hours).map(([day, hours]) => (
                      <div key={day} className="flex justify-between gap-4">
                        <span className="capitalize">{day}</span>
                        <span>{hours}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Social Links */}
          {business.business_social_links && Object.keys(business.business_social_links).length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="font-medium mb-3">Connect</div>
              <div className="flex gap-3">
                {Object.entries(business.business_social_links).map(([platform, url]) => {
                  const Icon = SOCIAL_ICONS[platform] || Globe;
                  return (
                    <a
                      key={platform}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Badges */}
        {badges.length > 0 && (
          <div className="bg-white border-2 border-black rounded-xl p-6 shadow-brutal mb-6">
            <h2 className="font-bold mb-4 flex items-center gap-2">
              🏆 Badges & Recognition
            </h2>
            <div className="flex flex-wrap gap-3">
              {badges.map((badge) => (
                <div key={badge.badge_id} className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg">
                  <span className="text-xl">{badge.icon}</span>
                  <span className="font-medium text-sm">{badge.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Hosted Events */}
        {hostedEvents.length > 0 && (
          <div className="bg-white border-2 border-black rounded-xl p-6 shadow-brutal">
            <h2 className="font-bold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" /> Upcoming Activities
            </h2>
            <div className="space-y-3">
              {hostedEvents.map((event) => (
                <Link
                  key={event.event_id}
                  to={`/event/${event.event_id}`}
                  className="block p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="font-medium">{event.title}</div>
                  <div className="text-sm text-slate-500 flex items-center gap-3 mt-1">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(event.start_date).toLocaleDateString()}
                    </span>
                    {event.attendee_count !== undefined && (
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {event.attendee_count} attending
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
